import { useMemo } from 'react'
import { Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Key } from 'react'
import {
  Student,
  StudentTableProps,
  StudentStatus,
} from '../types'
import { StudentStatusLabels, StudentStatusColors } from '../constants'
import dayjs from 'dayjs'
import { TableActionColumn } from '@/components/TableActionColumn'
import { ID } from '@/types/common'

const StudentTable = ({
  students,
  onEdit,
  onDelete,
  loading = false,
  selectedIds = [],
  onSelectionChange,
  pagination,
  onPageChange,
}: StudentTableProps) => {
  const columns: ColumnsType<Student> = useMemo(() => [
    {
      title: '学生姓名',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Student, b: Student) => a.name.localeCompare(b.name, 'zh'),
    },

    {
      title: '生日',
      dataIndex: 'birthday',
      key: 'birthday',
      render: (date?: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
      sorter: (a: Student, b: Student) => {
        if (!a.birthday && !b.birthday) return 0
        if (!a.birthday) return 1
        if (!b.birthday) return -1
        return dayjs(a.birthday).unix() - dayjs(b.birthday).unix()
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: StudentStatus) => {
        return (
          <Tag color={StudentStatusColors[status] || 'default'}>
            {StudentStatusLabels[status]}
          </Tag>
        )
      },
      filters: [
        { text: '干预中', value: StudentStatus.INTERVENING },
        { text: '已离园', value: StudentStatus.LEFT },
      ],
      onFilter: (value: unknown, record: Student) => record.status === value,
    },
    {
      title: '备注',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      render: (note: string) => note || '-',
    },
    TableActionColumn<Student>({
      onEdit,
      onDelete: (record: Student | ID) => {
        const id = typeof record === 'object' && record !== null ? (record as Student).id : record
        onDelete(id)
      },
      getId: (record) => record.id,
      deleteConfirmDescription: (record) => `确定要删除学生 "${record.name}" 吗？`,
      width: 200,
    }) as any,
  ], [onEdit, onDelete])

  const rowSelection = onSelectionChange
    ? {
        selectedRowKeys: selectedIds,
        onChange: (selectedRowKeys: Key[]) => {
          onSelectionChange(selectedRowKeys as ID[])
        },
        getCheckboxProps: (record: Student) => ({
          name: record.name,
        }),
      }
    : undefined

  return (
    <Table
      columns={columns}
      dataSource={students}
      rowKey="id"
      loading={loading}
      rowSelection={rowSelection}
      pagination={{
        current: pagination?.page,
        pageSize: pagination?.pageSize,
        total: pagination?.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        pageSizeOptions: ['10', '20', '50', '100'],
        onChange: (page, pageSize) => {
          onPageChange?.(page, pageSize)
        },
      }}
      size="middle"
      showSorterTooltip={false}
    />
  )
}

export default StudentTable
